import { ChangeEvent, useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { Autocomplete, Fade, FormControl } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { changeMaxNumberException, replaceRobotExceptionList, selectActiveRobotVersion } from "../../../store/ses/sesRobotSlice";
import { selectChannelList } from "../../../store/langSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { SES } from "../../../constants/accessRights";
import { ROBOT_EXCEPTION_LIST } from "../../../constants/robotConfigLists";
import { ExceptionType } from "../../../types/ses/sesRobotTypes";
import NoOptionText from "../../../ui/NoOptionText/NoOptionText";
import ActionEvent from "../../ActionEvent/ActionEvent";
import FormAddingAction from "../../Forms/FormAddingAction/FormAddingAction";
import { IExceptionsTabProps } from "./ExceptionsTab.props";
import styles from "./ExceptionsTab.module.scss";
import { RenderOption, TextfieldWithAutocomplete } from "@/ui";
import { useSearchActionsDataElemByConditionsAndCategoties } from "@/hooks";
import { useFormik } from "formik";
import { selectSesHints } from "@/store/hintsSlice";
import { CustomMenuItem, CustomTextField } from "@/ui";

const ExceptionsTab = ({
	robotData,
	changeFlg,
	changeFlgHandler,

	selectException,
	setSelectException,
	maxNumberExceptions,
	setMaxNumberExceptions,
	inputChannel,
	setInputChannel,
	editRobotHandler,
}: IExceptionsTabProps): JSX.Element => {
	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const channelList = useAppSelector(selectChannelList); // store - список каналов
	const [answeringMachineActive, setAnsweringMachineActive] = useState<boolean>(false);
	const formik = useFormik({
		initialValues: { searchByDataElem: "" },
		validateOnMount: false,
		validateOnChange: false,
		validateOnBlur: true,
		onSubmit: () => {},
	});

	const sesHints = useAppSelector(selectSesHints);

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за выбором исключения
	useEffect(() => {
		// if (visibleBlockConfig === "exceptions") {
		// если есть исключение и в нем отсутствует выбранный канал - выбираем первый из возможных
		// if (robotData.exceptions[selectException] && "actions" in robotData.exceptions[selectException] && !robotData.exceptions[selectException].actions[inputChannel]) {
		// 	setInputChannel(Object.keys(robotData.exceptions[selectException].actions)[0]);
		// }
		// если нет исключения - выбираем default канал
		// else if (!robotData.exceptions[selectException]) {
		// 	setInputChannel("default");
		// }
		// устанавливаем макс кол-во исключений подряд, если есть
		if (selectException === "answering machine") {
			if ("answering machine" in robotData.exceptions) {
				dispatch(changeMaxNumberException({ exception: selectException, max: robotData.exceptions["answering machine"].max }));
				setMaxNumberExceptions(robotData.exceptions["answering machine"].max);
				setAnsweringMachineActive(Boolean(robotData.exceptions["answering machine"].max));
			}

			// setInputChannel("voice");
		} else if (robotData.exceptions[selectException] && "max" in robotData.exceptions[selectException]) {
			setInputChannel("default");
			robotData.exceptions[selectException].max !== maxNumberExceptions && setMaxNumberExceptions(robotData.exceptions[selectException].max);
		} else {
			setInputChannel("default");
			setMaxNumberExceptions(0);
		}
		// }
	}, [selectException /*  visibleBlockConfig */]);

	// обработчик перемещения исключений
	const moveException = (dragIndex: number, hoverIndex: number) => {
		dispatch(
			replaceRobotExceptionList({
				exception: selectException,
				channel: inputChannel,
				actionList: update(robotData.exceptions[selectException].actions[inputChannel], {
					$splice: [
						[dragIndex, 1],
						[hoverIndex, 0, robotData.exceptions[selectException].actions[inputChannel][dragIndex]],
					],
				}),
			})
		); // изменение последовательности исключений
		changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
	};

	const changeAnsweringMachineActiveHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const value = Boolean(e.target.value);

		setAnsweringMachineActive(value);
		setMaxNumberExceptions(Number(value));
		dispatch(changeMaxNumberException({ exception: selectException, max: Number(value) }));
		changeFlgHandler("exceptions");
	};

	const { dataElemsAutocompleteOptions, visibleActionsIds, dataElemIn } = useSearchActionsDataElemByConditionsAndCategoties({
		data: robotData?.exceptions && Object.entries(robotData.exceptions).length > 0 ? robotData.exceptions[selectException]?.actions?.[inputChannel] ?? [] : [],
		searchInputValue: formik.values.searchByDataElem,
		setSearchInputValue: (values: { searchByDataElem: string }) => {
			formik.setValues(values);
		},
	});

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.exception}>
				<div className={styles.exceptionBlock}>
					{/* Тип */}
					<CustomTextField
						select
						fullWidth
						label={translate("select_type")}
						value={selectException}
						onChange={(e) => setSelectException(e.target.value as ExceptionType)}
						hint={sesHints.hint_sesBlockExceptionTypes}
						hintPosition="left"
					>
						{ROBOT_EXCEPTION_LIST.map(({ exception, translation }) => (
							<CustomMenuItem key={exception} value={exception}>
								{translate(translation)}{" "}
								{robotData.exceptions[exception]?.actions?.[inputChannel]?.length > 0 && `(${robotData.exceptions[exception]?.actions?.[inputChannel].length})`}
							</CustomMenuItem>
						))}
					</CustomTextField>

					{selectException === "answering machine" ? (
						// детектор
						<CustomTextField
							select
							fullWidth
							label={translate("select_detector")}
							value={Number(answeringMachineActive)}
							onChange={changeAnsweringMachineActiveHandler}
							disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						>
							<CustomMenuItem value={0}>{translate("selectItem_inactive")}</CustomMenuItem>
							<CustomMenuItem value={1}>{translate("selectItem_active")}</CustomMenuItem>
						</CustomTextField>
					) : (
						<>
							{/* канал */}
							<Autocomplete
								fullWidth
								freeSolo={isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft"}
								options={Array.from(
									new Set(
										Object.keys(robotData.exceptions[selectException]?.actions || {}).concat(
											channelList.data.map((channel) => channel.name),
											"default"
										)
									)
								)}
								value={inputChannel}
								onChange={(_, value) => setInputChannel(value ? value : "")}
								noOptionsText={<NoOptionText />}
								renderInput={(params) => (
									<CustomTextField
										{...params}
										hint={sesHints.hint_sesTabChannels}
										label={translate("input_channel")}
										onChange={(e) => isAccess(SES.ROBOT_EDIT) && setInputChannel(e.target.value)}
									/>
								)}
								renderOption={(props, option) => {
									return (
										<RenderOption {...props} key={option}>
											{option}
										</RenderOption>
									);
								}}
							/>

							{/* макс. кол-во исключений подряд */}
							<CustomTextField
								fullWidth
								required
								label={translate("input_maxCountExceptions")}
								type="Number"
								disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
								value={maxNumberExceptions}
								onChange={(e) => setMaxNumberExceptions(+e.target.value)}
								onBlur={() => {
									dispatch(changeMaxNumberException({ exception: selectException, max: maxNumberExceptions })); // изменияем максимальное количество исключений
									changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
								}}
								InputProps={{
									inputProps: { min: 1 },
								}}
							/>
						</>
					)}
				</div>

				<FormControl sx={{ m: "0 16px" }}>
					<TextfieldWithAutocomplete
						autocompleteOptionsWithId={dataElemsAutocompleteOptions}
						value={formik.values.searchByDataElem}
						setValues={formik.setValues}
						values={formik.values}
						onChange={(e) => formik.handleChange(e)}
						name="searchByDataElem"
						label={translate("input_filterByDataElements")}
					/>
				</FormControl>
				{/* действия */}
				<div className={styles.actions}>
					{selectException !== "answering machine" && (
						<>
							<DndProvider backend={HTML5Backend}>
								{robotData.exceptions[selectException]?.actions?.[inputChannel]?.map((action, idx, actionsArr) => (
									<ActionEvent
										key={action.actionId || JSON.stringify(action)}
										action={action}
										idx={idx}
										actionsArr={actionsArr}
										channel={inputChannel}
										isFilterActive={formik.values.searchByDataElem !== ""}
										searchedDataElemIn={dataElemIn}
										changeFlgHandler={changeFlgHandler}
										isActionVisible={
											visibleActionsIds.length !== 0
												? action.actionId
													? visibleActionsIds.includes(action.actionId)
													: visibleActionsIds.includes(`${action.action}-${idx}`)
												: true
										}
										actionFor={{
											for: "robotException",
											exception: selectException,
											isShownSaveButton: true,
											isAccessSaveButton: isAccess(SES.ROBOT_EDIT),
											disabledSaveButton: !changeFlg.thisIs,
											onClickSaveButtonHandler: editRobotHandler,
										}}
										moveAction={moveException}
									/>
								))}
							</DndProvider>

							{isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft" && (
								<FormAddingAction
									channel={inputChannel}
									changeFlgHandler={changeFlgHandler}
									actionFor={{
										for: "robotException",
										exception: selectException,
										isShownSaveButton: true,
										isAccessSaveButton: isAccess(SES.ROBOT_EDIT),
										disabledSaveButton: !changeFlg.thisIs,
										onClickSaveButtonHandler: editRobotHandler,
									}}
								/>
							)}
						</>
					)}
				</div>
			</div>
		</Fade>
	);
};

export default ExceptionsTab;
